import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import type { CrmContractorCandidate } from '@ip/shared-types';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { useCrmApi } from '@/services/api';
import { t } from '@/i18n';
import { ContractorSummaryLine } from './ContractorSummaryLine';

interface Props {
  nip: string;
  accountId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Fired after a successful link — caller reloads the account and closes. */
  onLinked: () => void;
}

/**
 * Candidate picker for the "Dopasuj" flow on the account's contractor
 * panel — lists existing contractors that look like the same counterparty
 * (same NIP/VAT id, or a similar name) so the account links to them instead
 * of `create-contractor` minting a duplicate row.
 */
export function ContractorMatchDialog({ nip, accountId, open, onOpenChange, onLinked }: Props) {
  const { listContractorCandidates, linkContractor } = useCrmApi();
  const [loading, setLoading] = useState(true);
  const [candidates, setCandidates] = useState<CrmContractorCandidate[]>([]);
  const [linkingId, setLinkingId] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    setCandidates([]);
    listContractorCandidates(nip, accountId)
      .then((result) => {
        if (!cancelled) setCandidates(result);
      })
      .catch(() => {
        if (!cancelled) toast.error(t('crm.accounts.contractor.loadFailed'));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, nip, accountId, listContractorCandidates]);

  const handleLink = async (candidate: CrmContractorCandidate) => {
    setLinkingId(candidate.id);
    try {
      await linkContractor(nip, accountId, candidate.id);
      toast.success(t('crm.accounts.contractor.linked'));
      onLinked();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t('errors.saveFailed'));
    } finally {
      setLinkingId(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('crm.accounts.contractor.match.title')}</DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="space-y-2">
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
          </div>
        ) : candidates.length === 0 ? (
          <p className="text-muted-foreground text-sm">{t('crm.accounts.contractor.match.empty')}</p>
        ) : (
          <ul className="space-y-2">
            {candidates.map((candidate) => (
              <li
                key={candidate.id}
                className="flex items-start justify-between gap-3 rounded-md border p-3 text-sm"
              >
                <div className="min-w-0 space-y-1">
                  <ContractorSummaryLine
                    contractor={candidate}
                    badge={
                      <Badge variant={candidate.match === 'exact' ? 'default' : 'outline'}>
                        {candidate.match === 'exact'
                          ? t('crm.accounts.contractor.match.exact')
                          : t('crm.accounts.contractor.match.name')}
                      </Badge>
                    }
                  />
                  {candidate.linkedAccountName ? (
                    <div className="text-xs text-amber-600 dark:text-amber-500">
                      {t('crm.accounts.contractor.linkedTo', { name: candidate.linkedAccountName })}
                    </div>
                  ) : null}
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  className="shrink-0"
                  // Jedno wiązanie naraz: blokujemy wszystkie wiersze, ale
                  // spinner kręci się tylko na tym, który kliknięto.
                  disabled={linkingId !== null}
                  onClick={() => handleLink(candidate)}
                >
                  {linkingId === candidate.id ? <Loader2 className="size-3.5 animate-spin" /> : null}
                  {t('crm.accounts.contractor.match.link')}
                </Button>
              </li>
            ))}
          </ul>
        )}
      </DialogContent>
    </Dialog>
  );
}
